/**
 *
 * DynamicSideList
 *
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

// antd component
import { Layout, PageHeader, Empty } from "antd";
const { Sider } = Layout;
// components
import Preloader from 'components/Preloader';
import SideListItem from 'components/SideListItem';
import {
  SortButton,
  AddButton,
  SearchButton,
  FilterOptions,
} from 'components/DynamicSideList/components';

// utils
import { getObjId } from "utils/helpers/helper.general";
import api from "utils/api";
// contexts
import { withTheme } from 'engine/Contexts/Theme.context';

function DynamicSideList({
  type,
  title,
  subTitle,
  sortProps,
  searchProps,
  filterProps,
  ascend,
  outputListItem,
  showPreventClass,
  firstRender,
  // main
  list,
  noMore,
  loadingList,
  totalAmount,
  initialID,
  activeElement,
  currentItemAction,
  loadParams,
  // actions
  changeParams,
  addAction,
  onClick,
  // contexts
  theme,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('side-list-wrapper', className);
  const { t, i18n } = useTranslation();
  const lang =
    localStorage.getItem("i18nextLng") ||
    process.env.DEFAULT_LANGUAGE ||
    "fr";

  const initSort = {
    ascend: ascend || false, // descend
    props: sortProps ? [...new Set([...sortProps])] : [], // 'description.firstname'
  };

  const scrollParentRef = useRef(null);

  const [showSearch, setShowSearch] = useState(
    loadParams?.searchStr.length > 0,
  );
  const [showFilter, setShowFilter] = useState(
    false /* || loadParams?.filterValue.length > 0, */,
  );

  const [currentParams, setCurrentParams] = useState(loadParams);

  const [searchVal, setSearchVal] = useState(loadParams?.searchStr || "");
  const [sort, setSort] = useState(initSort);
  const [filter, setFilter] = useState(
    loadParams?.filterValue?.length > 0
      ? typeof loadParams?.filterValue === "string"
        ? loadParams?.filterValue?.split(",")
        : loadParams?.filterValue
      : [],
  );
  const [language, setLanguage] = useState(lang);
  const [optionsForFilter, setOptionsForFilter] = useState([]);
  const [activeListItem, setActiveListItem] = useState(activeElement);

  let timeOutListScroll;

  // Get Types for filtering on First load
  useEffect(() => {
    setCurrentParams(loadParams);
    setLanguage(i18n.language);
    // load items for filtering
    if (optionsForFilter?.length <= 0) {
      api.communications.fetchAllTypes().then(result => {
        let emptyResult = [
          {
            key: "types",
            name: "types",
            group: [
              {
                key: "types",
                label: t("labels.types"),
                name: "types-all",
                value: "types-all",
              },
            ],
          },
        ];
        if (result?.length > 0) {
          setOptionsForFilter(result);
        } else {
          setOptionsForFilter(emptyResult);
        }
      });
    }

    return () => {
      // this will clear setTimeout
      timeOutListScroll && clearTimeout(timeOutListScroll);
    };
  }, []);

  // Get Types for filtering if language change
  useEffect(() => {
    // load items for filtering
    if (i18n.language != language) {
      setLanguage(i18n.language);

      api.communications.fetchAllTypes().then(result => {
        let emptyResult = [
          {
            key: "types",
            name: "types",
            group: [
              {
                key: "types",
                label: t("labels.types"),
                name: "types-all",
                value: "types-all",
              },
            ],
          },
        ];
        if (result?.length > 0) {
          setOptionsForFilter(result);
        } else {
          setOptionsForFilter(emptyResult);
        }
      });
    }
  }, [i18n.language]);

  // Scroll to Active list element
  /*  useEffect(() => {
    if (activeListItem || currentItem) {
      const elemID = activeListItem
        ? getObjId(activeListItem)
        : getObjId(currentItem);
      const domListElement = document.getElementById(
        `${type}-infinite-scroll-web`,
      );
      if (elemID && domListElement) {
          timeOutListScroll = setTimeout(() => {
          const domElement = document.getElementById(elemID);
          //  const offsetElem = domElement.offsetTop;
          domElement && domListElement.scrollTo(0, domElement.offsetTop);
        }, 600); 
      }
    }
  }, [activeListItem, currentItem]); */

  // set active list Item
  useEffect(() => {
    if (getObjId(initialID) != getObjId(activeListItem)) {
      if (getObjId(initialID) == getObjId(activeElement)) {
        setFirstElemInArray(activeElement);
      } else {
        const listEl = list.find(el => getObjId(initialID) == getObjId(el));
        !!listEl
          ? setFirstElemInArray(listEl)
          : setFirstElemInArray(initialID);
      }
    } else if (!activeElement && list?.length > 0) {
      const firstElem = list[0];
      setFirstElemInArray(firstElem);
    }
  }, [initialID]);

  const setFirstElemInArray = firstElem => {
    if (firstElem) {
      const firstElemID = getObjId(firstElem);
      setActiveListItem(firstElem);
      currentItemAction(firstElemID, firstElem);
    }
  };

  // change active list item
  const changeCurrentItem = (elementId, element) => {
    if (getObjId(activeListItem) != getObjId(element)) {
      setActiveListItem(element);
      currentItemAction(elementId, element);
    }
  };

  /**
   * Search Actions
   */
  const onSearch = useCallback(
    searchValue => {
      if (!loadingList) {
        setCurrentParams(prev => {
          const newVal = {
            ...prev,
            page: 1,
            searchStr: searchValue,
          };
          changeParams(newVal);
          return newVal;
        });
      }
    },
    [loadingList],
  );
  const onCloseSearch = useCallback(() => {
    setSearchVal("");
    setCurrentParams(prev => {
      const newVal = { ...prev, searchStr: "" };
      changeParams(newVal);
      return newVal;
    });
  }, []);

  /**
   * Sort Actions
   */
  const onSort = useCallback(() => {
    const newSort = { ...sort, ascend: !sort.ascend };
    setSort(newSort);
    // const sortedList = getInitSortedList(list, newSort);
    const firstElem = list?.length > 0 && list[0];
    setFirstElemInArray(firstElem);
  }, [list]);

  /**
   * Add Actions
   */
  const onAddAction = useCallback(() => {
    setShowSearch(false);
    setShowFilter(false);
    setSearchVal("");
    setSort(initSort);
    setFilter([]);
    const firstElem = list?.length > 0 && list[0];
    setFirstElemInArray(firstElem);
    addAction();
  }, [list, initSort]);

  /**
   * Filter Actions
   */
  const onFilter = useCallback(checkedValues => {
    setFilter(checkedValues || []);
    setCurrentParams(prev => {
      const newVal = {
        ...prev,
        page: 1,
        filterValue: checkedValues,
      };
      changeParams(newVal);
      return newVal;
    });
  }, []);

  const resetFilter = useCallback(() => {
    setShowSearch(false);
    setSearchVal("");
    setFilter([]);
    setCurrentParams(prev => {
      const newVal = {
        ...prev,
        page: 1,
        searchStr: "",
        filterValue: [],
      };
      changeParams(newVal);
      return newVal;
    });
  }, [list]);

  // extra actions for side header
  const getExtraActions = useCallback(() => {
    const extraActions = [
      <SearchButton
        key="search-communication-bar"
        uniqueKey="search-communication"
        value={searchVal}
        loading={loadingList}
        show={showSearch}
        setShow={setShowSearch}
        onSearch={onSearch}
        onClose={onCloseSearch}
        onClick={() => setShowFilter(false)}
      />,
      <FilterOptions
        uniqueKey="filter-communication"
        view="list"
        value={filter}
        loading={loadingList}
        options={optionsForFilter}
        show={showFilter}
        setShow={setShowFilter}
        onFilter={onFilter}
        onReset={resetFilter}
      />,
      sortProps && (
        <SortButton
          key="sort-option-bar"
          uniqueKey="sort-communication"
          onSort={onSort}
          sortByAsc={sort.ascend}
        />
      ),
      <AddButton
        key="add-btn-bar"
        uniqueKey="addNew-communication"
        onClick={onAddAction}
      />,
    ];
    return extraActions;
  }, [
    searchVal,
    loadingList,
    showSearch,
    filter,
    loadingList,
    optionsForFilter,
    showFilter,
    sort.ascend,
    sortProps,
    i18n?.language,
  ]);

  const getSubTitleLabel = useCallback(
    type => {
      switch (type) {
        case "users":
        case "utilisateurs":
          return i18n?.language == "de" ? t("labels.users") : t("labels.users")?.toLocaleLowerCase();

        case "etudiants":
          return i18n?.language == "de" ? t("labels.students") : t("labels.students")?.toLocaleLowerCase();

        case "classes":
          return i18n?.language == "de" ? t("labels.classes") : t("labels.classes")?.toLocaleLowerCase();

        case "сours":
        case "сourses":
        case "courses":
          return i18n?.language == "de" ? t("labels.courses") : t("labels.courses")?.toLocaleLowerCase();

        case "news":
        case "communications":
          return i18n?.language == "de" ? t("labels.communications") : t("labels.communications")?.toLocaleLowerCase();

        default:
          return type;
      }
    },
    [i18n?.language],
  );

  const loadMoreCallback = useCallback(
    (loadingList, noMore) => {
      if (!loadingList && !noMore) {
        setCurrentParams(prev => {
          const newVal = {
            ...prev,
            page: Number(loadParams?.page) + 1,
          };
          changeParams(newVal);
          return newVal;
        });
      }
    },
    [loadParams],
  );

  const showOpenOnSaveModalCallback = useCallback(() => {
    theme?.onOpenOnSaveModalCallback() 
    !!onClick && onClick();
  }, [theme?.isOpenModalOnSaveChanges, theme?.hasValueModalOnSaveChanges, onClick]);

  return (
    <Sider
      className={childClassNames}
      onClick={showOpenOnSaveModalCallback}
      ref={scrollParentRef}
    >
      <PageHeader
        className={`side-list-header ${showSearch ? "searchIsShowing" : ""}`}
        title={(!!title && title) || t("labels.listOf")}
        subTitle={
          !!subTitle
            ? subTitle
            : `${totalAmount || list?.length || 0} ${getSubTitleLabel(type)}`
        }
        extra={getExtraActions()}
      />
      <div
        key={`${type}-infinite-scroll-web`}
        id={`${type}-infinite-scroll-web`}
        className="infinite-scroll-web"
      >
        <InfiniteScroll
          key={`${type}-dyncamic-side-list`}
          id={`${type}-dyncamic-side-list`}
          className="dyncamic-side-list"
          initialLoad={false}
          pageStart={loadParams?.page || 1}
          loadMore={() => loadMoreCallback(loadingList, noMore)}
          hasMore={!noMore}
          useWindow={false}
          getScrollParent={function() {
            const parentRefContainer = document.querySelector(
              ".infinite-scroll-web",
            );
            return parentRefContainer;
          }}
          loader={<Preloader />}
        >
          {list?.length > 0 &&
            list?.map(
              item =>
                item?._id && (
                  <SideListItem
                    key={item?._id + "-dyncamic-side-item"}
                    type={type}
                    onClickAction={changeCurrentItem}
                    element={item}
                    activeElement={activeElement}
                    searchString={searchVal}
                  />
                ),
            )}
          {!loadingList && list?.length <= 0 && (
            <Empty
              key="empty-list-message"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("errors.noDataOnRequest")}
            />
          )}
        </InfiniteScroll>
      </div>
    </Sider>
  );
}

DynamicSideList.defaultProps = {
  type: 'utilisateurs',
  subTitle: '',
  ascend: true,
};
DynamicSideList.propTypes = {
  type: PropTypes.oneOf([
    'utilisateurs',
    'classes',
    'сours',
    'etudiants',
    'absence',
    'communications',
  ]).isRequired,
  list: PropTypes.oneOfType([
    PropTypes.array.isRequired,
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
  ]),
  sortProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  searchProps: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  ascend: PropTypes.bool,
  currentItemAction: PropTypes.func.isRequired,
  addAction: PropTypes.func.isRequired,
};

export default withTheme(DynamicSideList);
